import { UniqueIdentifier } from '@dnd-kit/core';
import { HTMLProps, useEffect, useRef, useState } from 'react';
import { LangType } from '../../helpers/lang';
import { useClickOutside } from '../../hooks/use-click-outside';
import { useLang } from '../../hooks/use-lang';
import { useToggle } from '../../hooks/use-toggle';
import { useInputLangStore } from '../../store/use-input-lang-store';

export interface EditableLabelProps
  extends Omit<HTMLProps<HTMLInputElement>, 'value' | 'size' | 'id'> {
  onConfirm?: (value?: string | LangType) => Promise<void>;
  onDelete?: () => Promise<void>;
  value?: string | LangType;
  sortable?: boolean;
  id?: UniqueIdentifier;
  isDisabled?: boolean;
}

export const useEditableLabel = (props: EditableLabelProps) => {
  const { t } = useLang();
  const { onConfirm, value, sortable, id, onDelete, isDisabled, ...other } = props;
  const [isEditing, , setIsEditing] = useToggle(false);
  const [editValue, setEditValue] = useState<string | LangType>();
  const { getLocale } = useLang();
  const { language } = useInputLangStore();
  const editRef = useRef<HTMLUListElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };
  const stopEditing = () => setIsEditing(false);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleConfirm = async () => {
    try {
      await onConfirm?.(editValue);
    } catch (e) {
      console.error(e);
      setEditValue(value);
    } finally {
      stopEditing();
    }
  };

  const handleCancel = () => {
    stopEditing();
    setEditValue(value);
  };

  useClickOutside(
    editRef,
    () => {
      if (isEditing) handleCancel();
    },
    [isEditing],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') handleCancel();
  };

  const isLangType = typeof editValue === 'object';

  const label = isLangType ? getLocale(editValue, language) : editValue;

  return {
    t,
    isEditing,
    editValue,
    setEditValue,
    handleEdit,
    handleConfirm,
    onKeyDown,
    editRef,
    displayRef,
    isLangType,
    other,
    sortable,
    id,
    label,
    onDelete,
    isDisabled,
  };
};
